import {Box, Paper, Typography} from '@mui/material';
import {DataGrid, GridColDef} from '@mui/x-data-grid';
import React, {useMemo} from 'react';
import {Profile} from '../../interfaces';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

interface Props {
  individualProfiles: Profile[];
  teamProfiles: Profile[];
}

const OrganizationProfilesTable: React.FC<Props> = ({
  individualProfiles,
  teamProfiles,
}) => {
  const profiles: (Profile & {
    isIndividual: boolean;
    isTeam: boolean;
  })[] = useMemo(() => {
    const output = [];
    for (const profile of individualProfiles) {
      output.push({
        ...profile,
        isIndividual: true,
        isTeam: false,
      });
    }
    for (const profile of teamProfiles) {
      // if id is the same then use profile information from individualProfiles
      const existingProfile = output.find(p => p.id === profile.id);
      if (existingProfile) {
        existingProfile.isTeam = true;
      } else {
        output.push({
          ...profile,
          isIndividual: false,
          isTeam: true,
        });
      }
    }
    // return output sorted by id
    return output.sort((a, b) => (a.id as number) - (b.id as number));
  }, [individualProfiles, teamProfiles]);

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 90,
      filterable: true,
    },
    {
      field: 'isIndividual',
      headerName: 'Individual',
      width: 90,
      filterable: true,
      renderCell: params => {
        return params.row.isIndividual ? (
          <CheckBoxIcon />
        ) : (
          <CheckBoxOutlineBlankIcon />
        );
      },
    },
    {
      field: 'isTeam',
      headerName: 'Team',
      width: 90,
      filterable: true,
      renderCell: params => {
        return params.row.isTeam ? (
          <CheckBoxIcon />
        ) : (
          <CheckBoxOutlineBlankIcon />
        );
      },
    },
    {
      field: 'firstName',
      headerName: 'First Name',
      width: 300,
      filterable: true,
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      filterable: true,
    },
    {
      field: 'email',
      headerName: 'Email',
      filterable: true,
    },
    {
      field: 'birthYear',
      headerName: 'Birth Year',
      filterable: true,
    },
    {
      field: 'gender',
      headerName: 'Gender',
      filterable: true,
    },
    {
      field: 'role',
      headerName: 'Role',
      filterable: true,
    },
    {
      field: 'membershipLevel',
      headerName: 'Membership Level',
      filterable: true,
    },
    {
      field: 'membershipTrialEndDate',
      headerName: 'Membership Trial End Date',
      filterable: true,
    },
    {
      field: 'lastSessionDate',
      headerName: 'Last Session Date',
      filterable: true,
    },
  ];

  return (
    <Box>
      <Typography variant="h4" sx={{my: 2}}>
        Profiles ({profiles.length})
      </Typography>
      <Paper sx={{height: 700}}>
        <DataGrid rows={profiles ?? []} columns={columns} />
      </Paper>
    </Box>
  );
};

export default React.memo(OrganizationProfilesTable);
